import { showAppToast } from '@/support/toastBus';

export type BulkRowId = string | number;

export interface BulkActionIssue {
  id: BulkRowId;
  code: string;
  message: string;
}

export interface BulkActionResult {
  action: string;
  outcome: 'success' | 'partial' | 'failed';
  requested_count: number;
  succeeded_count: number;
  skipped_count: number;
  failed_count: number;
  succeeded_ids: BulkRowId[];
  skipped: BulkActionIssue[];
  failures: BulkActionIssue[];
}

export interface BulkActionConfirmation {
  header?: string;
  message: string;
  acceptLabel?: string;
  acceptClass?: string;
}

interface ExecuteBulkActionOptions {
  selectedIds: BulkRowId[];
  confirmAction: (confirmation: BulkActionConfirmation) => Promise<boolean>;
  confirmation: (count: number) => BulkActionConfirmation;
  request: (ids: BulkRowId[]) => Promise<unknown>;
  refresh: () => Promise<unknown> | unknown;
  itemLabel?: string;
  limit?: number;
}

interface ViewBulkActionOptions {
  url: string;
  method: 'patch' | 'delete';
  confirmation: (count: number) => BulkActionConfirmation;
  refresh: () => Promise<unknown> | unknown;
  itemLabel?: string;
}

interface BulkActionViewContext {
  bulkSelectedIds: BulkRowId[];
  bulkActionPending: boolean;
  $confirmAction: (confirmation: BulkActionConfirmation) => Promise<boolean>;
  $api: {
    delete: (url: string, config: Record<string, unknown>) => Promise<unknown>;
    patch: (url: string, data: unknown, config: Record<string, unknown>) => Promise<unknown>;
  };
}

export interface BulkActionExecution {
  executed: boolean;
  selectedIds: BulkRowId[];
  result?: BulkActionResult;
}

export const BULK_ACTION_LIMIT = 500;

const idKey = (id: BulkRowId): string => String(id);

export const uniqueBulkIds = (ids: BulkRowId[]): BulkRowId[] => {
  const seen = new Set<string>();

  return (Array.isArray(ids) ? ids : []).filter((id) => {
    if (id === null || id === undefined) return false;

    const key = idKey(id);
    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

const issueList = (value: unknown): BulkActionIssue[] => {
  if (!Array.isArray(value)) return [];

  return value
    .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object')
    .map((entry) => ({
      id: entry.id as BulkRowId,
      code: String(entry.code || 'unknown'),
      message: String(entry.message || 'Action non effectuée.'),
    }))
    .filter((entry) => entry.id !== null && entry.id !== undefined);
};

export const normalizeBulkActionResult = (
  response: unknown,
  requestedIds: BulkRowId[],
): BulkActionResult => {
  const envelope = response as { data?: unknown } | undefined;
  const firstData = envelope?.data as { data?: unknown } | undefined;
  const payload = (firstData?.data || firstData || {}) as Record<string, unknown>;
  const succeededIds = uniqueBulkIds(Array.isArray(payload.succeeded_ids) ? payload.succeeded_ids as BulkRowId[] : []);
  const skipped = issueList(payload.skipped);
  const failures = issueList(payload.failures);
  const requestedCount = Number(payload.requested_count ?? requestedIds.length);
  const succeededCount = Number(payload.succeeded_count ?? succeededIds.length);
  const skippedCount = Number(payload.skipped_count ?? skipped.length);
  const failedCount = Number(payload.failed_count ?? failures.length);
  const inferredOutcome = failedCount === 0
    ? 'success'
    : succeededCount + skippedCount > 0
      ? 'partial'
      : 'failed';
  const rawOutcome = String(payload.outcome || inferredOutcome);

  return {
    action: String(payload.action || ''),
    outcome: ['success', 'partial', 'failed'].includes(rawOutcome)
      ? rawOutcome as BulkActionResult['outcome']
      : inferredOutcome,
    requested_count: Number.isFinite(requestedCount) ? requestedCount : requestedIds.length,
    succeeded_count: Number.isFinite(succeededCount) ? succeededCount : succeededIds.length,
    skipped_count: Number.isFinite(skippedCount) ? skippedCount : skipped.length,
    failed_count: Number.isFinite(failedCount) ? failedCount : failures.length,
    succeeded_ids: succeededIds,
    skipped,
    failures,
  };
};

export const remainingBulkIds = (
  selectedIds: BulkRowId[],
  result: BulkActionResult,
): BulkRowId[] => {
  const processedIds = new Set([
    ...result.succeeded_ids.map(idKey),
    ...result.skipped.map((entry) => idKey(entry.id)),
  ]);

  return uniqueBulkIds(selectedIds).filter((id) => !processedIds.has(idKey(id)));
};

const showBulkResultToast = (result: BulkActionResult, itemLabel: string): void => {
  const processedCount = result.succeeded_count + result.skipped_count;
  const parts = [`${processedCount}/${result.requested_count} ${itemLabel}(s) traitée(s)`];

  if (result.skipped_count > 0) parts.push(`${result.skipped_count} déjà conforme(s)`);
  if (result.failed_count > 0) parts.push(`${result.failed_count} en échec`);

  showAppToast({
    type: result.outcome === 'success' ? 'success' : result.outcome === 'partial' ? 'warning' : 'error',
    message: `${parts.join(', ')}.`,
    duration: result.failed_count > 0 ? 6500 : undefined,
  });
};

export const executeBulkAction = async (
  options: ExecuteBulkActionOptions,
): Promise<BulkActionExecution> => {
  const selectedIds = uniqueBulkIds(options.selectedIds);
  const limit = options.limit ?? BULK_ACTION_LIMIT;

  if (selectedIds.length === 0) {
    return { executed: false, selectedIds };
  }

  if (selectedIds.length > limit) {
    showAppToast({
      type: 'warning',
      message: `Réduisez la sélection à ${limit} lignes maximum avant de lancer cette action.`,
    });

    return { executed: false, selectedIds };
  }

  const confirmed = await options.confirmAction(options.confirmation(selectedIds.length));
  if (!confirmed) {
    return { executed: false, selectedIds };
  }

  const response = await options.request(selectedIds);
  const result = normalizeBulkActionResult(response, selectedIds);
  const remainingIds = remainingBulkIds(selectedIds, result);

  showBulkResultToast(result, options.itemLabel || 'ligne');
  try {
    await options.refresh();
  } catch (error) {
    console.error('Erreur de rafraîchissement après action groupée', error);
  }

  return {
    executed: true,
    selectedIds: remainingIds,
    result,
  };
};

export const bulkActionViewMixin = {
  data() {
    return {
      bulkSelectedIds: [] as BulkRowId[],
      bulkActionPending: false,
    };
  },
  computed: {
    bulkSelectionOverLimit(this: Pick<BulkActionViewContext, 'bulkSelectedIds'>): boolean {
      return this.bulkSelectedIds.length > BULK_ACTION_LIMIT;
    },
  },
  methods: {
    async performBulkAction(this: BulkActionViewContext, options: ViewBulkActionOptions): Promise<BulkActionExecution> {
      if (this.bulkActionPending) {
        return { executed: false, selectedIds: this.bulkSelectedIds };
      }

      this.bulkActionPending = true;

      try {
        const execution = await executeBulkAction({
          selectedIds: this.bulkSelectedIds,
          confirmAction: (confirmation) => this.$confirmAction(confirmation),
          confirmation: options.confirmation,
          request: (ids) => options.method === 'delete'
            ? this.$api.delete(options.url, {
              data: { ids },
              skipSuccessToast: true,
            })
            : this.$api.patch(options.url, { ids }, { skipSuccessToast: true }),
          refresh: options.refresh,
          itemLabel: options.itemLabel,
        });

        this.bulkSelectedIds = execution.selectedIds;
        return execution;
      } catch (error) {
        console.error('Erreur action groupée', error);
        return { executed: false, selectedIds: this.bulkSelectedIds };
      } finally {
        this.bulkActionPending = false;
      }
    },
  },
};
